import { Chip, SxProps, Theme } from '@mui/material'
import { StudyTimepointSubmission, TimepointSubmissionStatus } from '@common/config/api/client'
import { useUiStore } from '@features/qc_task_details_view/store/uiStore'

function getChipStyle(status: TimepointSubmissionStatus, isDisabled: boolean, isSelected: boolean): SxProps<Theme> {
  if (isDisabled) {
    return {
      backgroundColor: 'secondary.light',
      color: 'text.disabled',
      cursor: 'not-allowed',
    }
  }

  switch (status) {
    case 'NOT_STARTED':
      return {
        backgroundColor: 'status.notStarted',
        border: isSelected ? (theme) => `2px solid ${theme.palette.primary.main}` : '1px dashed #546E7A',
        color: 'text.contrast',
      }
    case 'IN_PROGRESS':
      return {
        backgroundColor: 'status.inProgress',
        border: isSelected ? (theme) => `2px solid ${theme.palette.primary.main}` : 'none',
        color: 'text.contrast',
      }
    case 'COMPLETE':
      return {
        backgroundColor: 'status.complete',
        border: isSelected ? (theme) => `2px solid ${theme.palette.primary.main}` : 'none',
        color: 'text.contrast',
      }
    default:
      return {
        backgroundColor: 'status.noStarted',
        border: isSelected ? (theme) => `2px solid ${theme.palette.primary.main}` : 'none',
        color: 'text.contrast',
      }
  }
}

export interface TimepointStatusProps {
  timepoint: StudyTimepointSubmission
  isDisabled?: boolean
}

export default function TimepointSubmissionChip({ timepoint, isDisabled = false }: TimepointStatusProps) {
  const { setTempSelectedTimepointSubmission, selectedTempTimepointSubmissionId } = useUiStore()

  // Use the uiStore's selected timepoint to determine if this chip is selected
  const isCurrentlySelected = selectedTempTimepointSubmissionId === timepoint.timepoint_submission_id

  const handleClick = () => {
    if (isDisabled) return

    // Update temporary selection in uiStore instead of directly setting global context
    setTempSelectedTimepointSubmission(
      timepoint.timepoint_submission_id,
      timepoint.name,
      null, // subjectPseudoId will be set from the parent component
    )
  }

  return (
    <Chip
      data-testid="timepoint-status-chip"
      label={timepoint.name}
      size="small"
      disabled={isDisabled}
      onClick={!isDisabled ? handleClick : undefined}
      sx={{
        ...getChipStyle(timepoint.status, isDisabled, isCurrentlySelected),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        width: 'auto',
        height: '32px',
        '&.Mui-disabled': {
          opacity: 0.7,
          pointerEvents: 'none',
        },
      }}
    />
  )
}
